import { useLocation } from 'react-router-dom';
import { Link } from './Link';
import { publicHref } from '../lib/seo';
import type { ReactNode } from 'react';

/**
 * Tiny inline "?" indicator with a hover/focus popover. Used on plan-card line items so
 * the meaning of "10,000 verification credits" or "12,000 emails / month" is one hover
 * away — without forcing the user to scroll into the explainer section.
 *
 * Optionally renders a "Learn more →" link in the popover that anchors into the
 * `PlansExplainer`'s `#how-credits-work` section, giving curious users the deeper dive
 * without enlarging the plan card itself.
 *
 * IMPORTANT — hover dead-zone: the visible card is offset DOWN with `pt-2` *on the
 * positioned wrapper*, not with `mt-` on the card itself. CSS `:hover` is lost when
 * the cursor leaves the bounding box of every descendant of the trigger group. A
 * `mt-` gap would create a 6-8px transparent strip between the trigger and the card
 * where neither element is hovered, so the popover would hide before the user could
 * reach the "Learn more" link. Making the gap *padding on the popover wrapper*
 * keeps it inside the wrapper's bounding box, so the hover bridges cleanly.
 */
export function InfoHint({
  children,
  learnMoreTo,
}: {
  children: ReactNode;
  /** Optional in-page anchor or route for the "Learn more" link. */
  learnMoreTo?: string;
}) {
  // A bare `#anchor` is resolved by react-router against the MATCHED ROUTE path, which is the
  // bare form (`pricing`) — so it rendered `/pricing#how-credits-work`, a href that 301s. Resolve
  // it against the normalised pathname instead. Prerender sees `/pricing` and the hydrated client
  // sees `/pricing/`; publicHref collapses both to `/pricing/`, so the href matches on both sides
  // and there is no hydration mismatch. Non-anchor values pass through to <Link>, which
  // normalises them itself.
  const { pathname } = useLocation();
  const resolved =
    learnMoreTo && learnMoreTo.startsWith('#') ? `${publicHref(pathname)}${learnMoreTo}` : learnMoreTo;
  return (
    <span className="group/hint relative inline-flex">
      <button
        type="button"
        aria-label="More information"
        className="ml-1 inline-grid h-3.5 w-3.5 cursor-help place-items-center rounded-full border border-faint text-[9px] font-bold text-faint transition hover:border-accent hover:text-accent focus:outline-none focus-visible:border-accent focus-visible:text-accent"
        tabIndex={0}
      >
        ?
      </button>
      {/* Positioned wrapper carries the visual gap via padding-top — keeps the cursor
          inside the group while it traverses from trigger to card, so the popover
          doesn't auto-hide mid-traversal. */}
      <span
        role="tooltip"
        className="pointer-events-none invisible absolute left-0 top-full z-50 w-64 pt-2 opacity-0 transition-opacity group-hover/hint:visible group-hover/hint:pointer-events-auto group-hover/hint:opacity-100 group-focus-within/hint:visible group-focus-within/hint:pointer-events-auto group-focus-within/hint:opacity-100"
      >
        <span className="block rounded-lg border border-border bg-panel p-3 text-[12px] leading-relaxed text-muted shadow-lg">
          {children}
          {learnMoreTo && (
            <Link
              to={resolved!}
              className="mt-2 block text-[11.5px] font-medium text-accent hover:underline"
            >
              Learn more →
            </Link>
          )}
        </span>
      </span>
    </span>
  );
}

